import { useState } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { ArrowLeft, Building2, ShieldCheck, CheckCircle, Loader2 } from "lucide-react";
import { z } from "zod";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

const dealerRegistrationSchema = z.object({
  abn: z.string().regex(/^\d{11}$/, "ABN은 11자리 숫자여야 합니다"),
  dealer_licence: z.string().optional(),
  plan: z.enum(["basic", "plus", "premium"]).default("basic"),
});

type DealerRegistrationForm = z.infer<typeof dealerRegistrationSchema>;

interface DealerPlan {
  id: "basic" | "plus" | "premium";
  name: string;
  price: number; // AUD per month
  features: string[];
  maxListings?: number;
  highlighted?: boolean;
}

const dealerPlans: DealerPlan[] = [
  {
    id: "basic",
    name: "Basic",
    price: 49,
    features: [
      "월 5개 차량 등록",
      "기본 프로필 표시",
      "ABN 인증 배지",
    ],
  },
  {
    id: "plus",
    name: "Plus",
    price: 99,
    features: [
      "월 15개 차량 등록",
      "프리미엄 프로필 표시",
      "ABN 인증 배지",
      "상단 노출 우선순위",
    ],
    highlighted: true,
  },
  {
    id: "premium",
    name: "Premium",
    price: 199,
    features: [
      "무제한 차량 등록",
      "프리미엄 프로필 표시",
      "ABN 인증 배지",
      "최우선 상단 노출",
      "전용 딜러 페이지",
      "통계 대시보드",
    ],
  },
];

export default function DealerRegistration() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [abnVerifying, setAbnVerifying] = useState(false);
  const [abnVerified, setAbnVerified] = useState(false);
  const [abnVerificationError, setAbnVerificationError] = useState<string | null>(null);
  const [verifiedAbnData, setVerifiedAbnData] = useState<{
    abn: string;
    entityName?: string;
    status?: string;
  } | null>(null);

  const form = useForm<DealerRegistrationForm>({
    resolver: zodResolver(dealerRegistrationSchema),
    defaultValues: {
      abn: "",
      dealer_licence: "",
      plan: "basic",
    },
  });

  // ABN 검증
  const verifyABN = async (abn: string) => {
    if (!abn || !/^\d{11}$/.test(abn.replace(/\s+/g, ""))) {
      setAbnVerificationError("ABN은 11자리 숫자여야 합니다");
      return;
    }

    setAbnVerifying(true);
    setAbnVerificationError(null);
    setAbnVerified(false);

    try {
      const res = await fetch("/api/trust/verify-abn", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ abn: abn.replace(/\s+/g, "") }),
      });

      const data = await res.json();

      if (data.valid) {
        setAbnVerified(true);
        setVerifiedAbnData({
          abn: data.abn,
          entityName: data.entityName,
          status: data.status,
        });
        form.setValue("abn", data.abn);
      } else {
        setAbnVerificationError(data.error || "ABN 검증에 실패했습니다");
        setAbnVerified(false);
      }
    } catch (error) {
      console.error("ABN verification error:", error);
      setAbnVerificationError("ABN 검증 중 오류가 발생했습니다");
      setAbnVerified(false);
    } finally {
      setAbnVerifying(false);
    }
  };

  const registerMutation = useMutation({
    mutationFn: async (data: DealerRegistrationForm) => {
      const token = await user?.getIdToken();
      return apiRequest(
        "POST",
        "/api/cars/dealer-register",
        {
          abn: data.abn,
          dealer_licence: data.dealer_licence || undefined,
          plan: data.plan,
          user_id: user?.uid,
        },
        {
          Authorization: `Bearer ${token}`,
        }
      );
    },
    onSuccess: async (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/me"] });

      // 플랜 선택 시 결제 페이지로 이동 (현재는 테스트 모드로 바로 등록)
      const selectedPlan = form.getValues("plan");

      if (selectedPlan !== "basic") {
        // Plus 또는 Premium 플랜은 결제 필요
        toast({
          title: "딜러 등록 완료",
          description: `${selectedPlan.toUpperCase()} 플랜을 선택하셨습니다. 결제 페이지로 이동합니다.`,
        });
        // TODO: 결제 페이지로 이동
        // setLocation(`/cars/dealer/payment?tier=${selectedPlan}`);
      } else {
        toast({
          title: "딜러 등록 완료",
          description: "Basic 플랜으로 등록되었습니다. 언제든지 플랜을 업그레이드할 수 있습니다.",
        });
      }

      setLocation("/cars/new");
    },
    onError: (error: any) => {
      toast({
        title: "등록 실패",
        description: error.message || "딜러 등록 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: DealerRegistrationForm) => {
    if (!abnVerified) {
      toast({
        title: "ABN 검증 필요",
        description: "먼저 ABN을 검증해주세요.",
        variant: "destructive",
      });
      return;
    }

    registerMutation.mutate(data);
  };

  if (!user) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">
            딜러 등록을 하려면 로그인이 필요합니다.
          </p>
          <Button onClick={() => setLocation("/")}>홈으로</Button>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer size="default">
      <Button
        variant="ghost"
        onClick={() => setLocation("/cars")}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        목록으로
      </Button>

      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              딜러 등록
            </CardTitle>
            <CardDescription>
              ABN을 인증하여 딜러로 등록하고 중고차를 판매하세요.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* ABN 입력 및 검증 */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="abn">ABN (Australian Business Number) *</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="abn"
                      placeholder="11자리 숫자 (예: 12345678901)"
                      maxLength={11}
                      {...form.register("abn")}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        form.setValue("abn", value);
                        setAbnVerified(false);
                        setVerifiedAbnData(null);
                      }}
                    />
                    <Button
                      type="button"
                      onClick={() => verifyABN(form.getValues("abn"))}
                      disabled={abnVerifying || !form.getValues("abn")}
                    >
                      {abnVerifying ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          검증 중...
                        </>
                      ) : (
                        "ABN 검증"
                      )}
                    </Button>
                  </div>
                  {form.formState.errors.abn && (
                    <p className="text-sm text-destructive mt-1">
                      {form.formState.errors.abn.message}
                    </p>
                  )}
                </div>

                {/* ABN 검증 결과 */}
                {abnVerified && verifiedAbnData && (
                  <Alert className="border-green-200 bg-green-50 dark:bg-green-950">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertTitle className="text-green-900 dark:text-green-100">
                      ABN 검증 완료
                    </AlertTitle>
                    <AlertDescription className="text-green-800 dark:text-green-200">
                      <p className="font-semibold">{verifiedAbnData.entityName || "인증된 사업자"}</p>
                      <p className="text-sm">ABN: {verifiedAbnData.abn}</p>
                      <p className="text-sm">상태: {verifiedAbnData.status || "Active"}</p>
                    </AlertDescription>
                  </Alert>
                )}

                {abnVerificationError && (
                  <Alert variant="destructive">
                    <AlertTitle>검증 실패</AlertTitle>
                    <AlertDescription>{abnVerificationError}</AlertDescription>
                  </Alert>
                )}
              </div>

              {/* 딜러 라이센스 (선택) */}
              <div>
                <Label htmlFor="dealer_licence">딜러 라이센스 번호 (선택)</Label>
                <Input
                  id="dealer_licence"
                  placeholder="Dealer Licence Number"
                  {...form.register("dealer_licence")}
                  className="mt-2"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  호주 딜러 라이센스 번호가 있는 경우 입력해주세요.
                </p>
              </div>

              {/* 플랜 선택 */}
              <div className="space-y-4">
                <Label>딜러 플랜 선택</Label>
                <RadioGroup
                  value={form.watch("plan")}
                  onValueChange={(value) => form.setValue("plan", value as any)}
                  className="grid grid-cols-1 md:grid-cols-3 gap-4"
                >
                  {dealerPlans.map((plan) => (
                    <div key={plan.id} className="relative">
                      <RadioGroupItem
                        value={plan.id}
                        id={plan.id}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={plan.id}
                        className={`flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          plan.highlighted
                            ? "border-primary bg-primary/5"
                            : "border-muted hover:border-primary/50"
                        } ${
                          form.watch("plan") === plan.id
                            ? "border-primary bg-primary/10"
                            : ""
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold">{plan.name}</span>
                          {plan.highlighted && (
                            <Badge variant="default" className="text-xs">
                              추천
                            </Badge>
                          )}
                        </div>
                        <div className="text-2xl font-bold mb-2">
                          ${plan.price}
                          <span className="text-sm font-normal text-muted-foreground">
                            /월
                          </span>
                        </div>
                        <ul className="text-sm space-y-1 text-muted-foreground">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* 제출 버튼 */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={!abnVerified || registerMutation.isPending}
                  className="flex-1"
                >
                  {registerMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      등록 중...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      딜러로 등록하기
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocation("/cars")}
                >
                  취소
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* 안내 정보 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">딜러 등록 안내</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              • 딜러로 등록하면 중고차를 더 많은 고객에게 노출시킬 수 있습니다.
            </p>
            <p>
              • ABN 인증을 통해 신뢰도를 높이고, 프리미엄 기능을 이용할 수 있습니다.
            </p>
            <p>
              • 플랜은 언제든지 변경하거나 취소할 수 있습니다.
            </p>
            <p>
              • 결제는 등록 후 별도로 진행됩니다.
            </p>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}

